/**
 * T071: Prompt Content Editor Component
 * Editor for text/chat content with template variable highlighting
 */

import { useState } from 'react'
import { Plus, Trash, GripVertical } from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { PromptContent, ChatMessage } from '@arcanum/core'

interface PromptContentEditorProps {
  value: PromptContent
  onChange: (value: PromptContent) => void
  disabled?: boolean
}

export function PromptContentEditor({ value, onChange, disabled }: PromptContentEditorProps) {
  const [contentType, setContentType] = useState<'text' | 'chat'>(value.type === 'chat' ? 'chat' : 'text')

  const handleTypeChange = (type: 'text' | 'chat') => {
    setContentType(type)
    if (type === 'text') {
      onChange({ type: 'text', text: '' })
    } else {
      onChange({
        type: 'chat',
        messages: [{ role: 'system', content: '' }],
      })
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Label>Content Type</Label>
        <Select value={contentType} onValueChange={(v) => handleTypeChange(v as 'text' | 'chat')} disabled={disabled}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="text">Text</SelectItem>
            <SelectItem value="chat">Chat</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {contentType === 'text' ? (
        <TextContentEditor
          value={value.type === 'text' ? value.text : ''}
          onChange={(text) => onChange({ type: 'text', text })}
          disabled={disabled}
        />
      ) : (
        <ChatContentEditor
          value={value.type === 'chat' ? value.messages : []}
          onChange={(messages) => onChange({ type: 'chat', messages })}
          disabled={disabled}
        />
      )}

      <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
        <p className="font-medium">Template Variables</p>
        <p>Use <code className="rounded bg-background px-1">{'{{variable_name}}'}</code> for template variables.</p>
        <p>Optional default: <code className="rounded bg-background px-1">{'{{variable_name:default value}}'}</code></p>
      </div>
    </div>
  )
}

interface TextContentEditorProps {
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}

function TextContentEditor({ value, onChange, disabled }: TextContentEditorProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor="content">Content</Label>
      <Textarea
        id="content"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your prompt content here..."
        className="min-h-[200px] font-mono"
        disabled={disabled}
      />
      <VariableHighlight text={value} />
    </div>
  )
}

interface ChatContentEditorProps {
  value: ChatMessage[]
  onChange: (value: ChatMessage[]) => void
  disabled?: boolean
}

function ChatContentEditor({ value, onChange, disabled }: ChatContentEditorProps) {
  const handleAddMessage = () => {
    onChange([...value, { role: 'user', content: '' }])
  }

  const handleRemoveMessage = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const handleUpdateMessage = (index: number, updates: Partial<ChatMessage>) => {
    onChange(value.map((msg, i) => (i === index ? { ...msg, ...updates } : msg)))
  }

  return (
    <div className="space-y-4">
      {value.map((message, index) => (
        <div key={index} className="flex gap-2">
          <div className="flex items-start pt-2 text-muted-foreground">
            <GripVertical className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Select
                value={message.role}
                onValueChange={(role) => handleUpdateMessage(index, { role: role as ChatMessage['role'] })}
                disabled={disabled}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system">System</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="assistant">Assistant</SelectItem>
                </SelectContent>
              </Select>
              {value.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveMessage(index)}
                  disabled={disabled}
                >
                  <Trash className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Textarea
              value={message.content}
              onChange={(e) => handleUpdateMessage(index, { content: e.target.value })}
              placeholder={`${message.role} message...`}
              className="min-h-[100px] font-mono"
              disabled={disabled}
            />
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={handleAddMessage} disabled={disabled}>
        <Plus className="mr-2 h-4 w-4" />
        Add Message
      </Button>
    </div>
  )
}

/**
 * T081: Template variable highlighting
 */
interface VariableHighlightProps {
  text: string
}

function VariableHighlight({ text }: VariableHighlightProps) {
  const variablePattern = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)(?::([^}]*))?\}\}/g
  const matches = [...text.matchAll(variablePattern)]

  if (matches.length === 0) {
    return null
  }

  const variables = matches.map((match) => ({
    name: match[1],
    defaultValue: match[2] || null,
  }))

  // Deduplicate by name
  const uniqueVariables = variables.filter(
    (v, i, arr) => arr.findIndex((x) => x.name === v.name) === i
  )

  return (
    <div className="flex flex-wrap gap-2">
      <span className="text-sm text-muted-foreground">Variables:</span>
      {uniqueVariables.map((variable) => (
        <span
          key={variable.name}
          className={cn(
            'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium',
            'bg-primary/10 text-primary'
          )}
        >
          {variable.name}
          {variable.defaultValue && (
            <span className="ml-1 text-muted-foreground">= {variable.defaultValue}</span>
          )}
        </span>
      ))}
    </div>
  )
}
